import { palette, spaces } from '@kimuno/theme';
import { Button, SvgIcon } from '@mui/material';
import config from 'config';
import { Link } from 'found';
import type React from 'react';
import { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { campaign, routes } from '../../constants';
import { selectUser } from '../../reducers/user.selectors';
import { getStripeUpgradeCheckoutUrl } from '../../routes/upgrade/utils';
import { ampliTrackMessageDisplayedEvent, MessageType } from '../../services/track.service';
import { bannerContext } from '../../state/BannerContextProvider';
import { isFeatureEnabled } from '../../utils/feature';
import getMuiIcon from '../../utils/getMuiIcon';
import { safeSessionStorage } from '../../utils/safeStorage';
import CustomMarketingAlert from '../CustomMarketingAlert';

const BANNER_TEXT_KEY =
	'Offer extended! More impact in class, more freedom in the afternoon. ' +
	'School start deal: {{percent}}% discount on meinUnterricht Pro';

export interface Offer {
	startDate: string;
	endDate: string;
	percent: number;
	promoCode: string;
}

export const getActiveOffer = (offer?: Offer, now: Date = new Date()): Offer | undefined => {
	if (!offer) {
		return undefined;
	}
	const today: number = new Date(now).setHours(0, 0, 0, 0);
	const startDate: number = new Date(offer.startDate).setHours(0, 0, 0, 0);
	const endDate: number = new Date(offer.endDate).setHours(0, 0, 0, 0);
	return today >= startDate && today <= endDate ? offer : undefined;
};

const UpgradeBanner: React.FC = () => {
	const { t } = useTranslation();
	const { bannerIsOpen, closeBanner } = useContext(bannerContext);
	const user = useSelector(selectUser);
	const activeOffer = getActiveOffer(config.features['upgradeBanner'].offer);

	const isVisible = Boolean(
		bannerIsOpen && isFeatureEnabled('upgradeBanner') && user?.isBasisUser && activeOffer
	);

	// Impression fires at most once per session so navigating between pages
	// (the banner remounts with each MasterLayout) doesn't inflate the count.
	const hasFiredImpression = useRef(false);
	useEffect(() => {
		if (hasFiredImpression.current || !isVisible) {
			return;
		}
		hasFiredImpression.current = true;
		const impressionKey = `banner-impression-${campaign.earlyBird2026}`;
		if (!safeSessionStorage.getItem(impressionKey)) {
			safeSessionStorage.setItem(impressionKey, 'true');
			ampliTrackMessageDisplayedEvent(campaign.earlyBird2026, MessageType.BANNER);
		}
	}, [isVisible]);

	if (!isVisible || !user || !activeOffer) {
		return null;
	}

	const utmParams = `utm_source=app&utm_medium=banner&utm_campaign=app-${campaign.earlyBird2026}`;
	// /payment admits only Zuora Basis users (SM-392); a Stripe Basis user would
	// be redirected to /premium and lose the promo code, so they go through the
	// resubscribe checkout instead.
	const ctaHref = user.isStripeUser
		? `${getStripeUpgradeCheckoutUrl('pro', activeOffer.promoCode)}&${utmParams}`
		: `/${routes.payment.root}?interval=monthly&plan=pro&promocode=${activeOffer.promoCode}&${utmParams}`;

	return (
		<CustomMarketingAlert
			isShow
			startIcon={
				<SvgIcon
					component={getMuiIcon('emojiNormal')}
					sx={{
						width: 22,
						height: 22,
						fill: palette.info.contrastText,
						marginRight: spaces.sm,
					}}
				/>
			}
			title={t(BANNER_TEXT_KEY, { percent: activeOffer.percent })}
			actionButton={
				<Button
					variant="contained"
					color="inherit"
					size="small"
					component={Link}
					to={ctaHref}
					sx={{ paddingX: spaces.sm }}
				>
					{t('Go to teacher discount')}
				</Button>
			}
			onClose={closeBanner}
		/>
	);
};

export default UpgradeBanner;
